import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDatabase } from '../context/DatabaseContext';
import { searchCertificates } from '../services/hrApiService';

const HRSearchPage = () => {
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const { getInstitutions } = useDatabase();
  const pageSize = 10;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery || selectedInstitution) {
        fetchCertificates();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedInstitution, page]);

  const fetchCertificates = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      // Use service function
      const data = await searchCertificates({
        institution_id: selectedInstitution,
        search: searchQuery.trim(),
        page: page,
        page_size: pageSize,
      });

      setCertificates(data.items || []);
      setTotalItems(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / pageSize));
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load certificates. Please try again.');
      setCertificates([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedInstitution, searchQuery, page]);

  const handleViewDetails = (certificate) => {
    setSelectedCertificate(certificate);
    setShowModal(true);
  };

  const handleDownload = () => {
    if (selectedCertificate?.ipfsUrl) {
      window.open(selectedCertificate.ipfsUrl, '_blank');
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 py-8 px-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <Link to="/hr-dashboard" className="inline-flex items-center gap-2 px-6 py-3 bg-white bg-opacity-20 backdrop-blur-md text-white hover:bg-opacity-30 rounded-xl transition-all mb-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 border border-white border-opacity-20">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-semibold">Back to Dashboard</span>
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-white to-purple-100 bg-opacity-25 backdrop-blur-md rounded-3xl flex items-center justify-center shadow-2xl border-4 border-white border-opacity-30 transform hover:rotate-6 transition-all duration-300">
              <svg className="w-12 h-12 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-6xl font-black text-white mb-2 drop-shadow-2xl tracking-tight bg-clip-text">Search Certificates</h1>
              <p className="text-purple-50 text-xl font-medium">🔍 Search and filter verified certificates from the database</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-6 border-2 border-white border-opacity-40 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 hover:bg-opacity-25 transform group">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white drop-shadow" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-purple-50 text-sm font-semibold uppercase tracking-wide">Total Results</p>
                  <p className="text-white text-3xl font-black drop-shadow-lg">{totalItems}</p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-6 border-2 border-white border-opacity-40 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 hover:bg-opacity-25 transform group">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                </div>
                <div>
                  <p className="text-purple-50 text-sm font-semibold uppercase tracking-wide">Institutions</p>
                  <p className="text-white text-3xl font-black drop-shadow-lg">{getInstitutions().length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-6 border-2 border-white border-opacity-40 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 hover:bg-opacity-25 transform group">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-red-600 rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-purple-50 text-sm font-semibold uppercase tracking-wide">Current Page</p>
                  <p className="text-white text-3xl font-black drop-shadow-lg">{page}/{totalPages || 1}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Card */}
        <div className="bg-gradient-to-br from-white via-purple-50 to-pink-50 rounded-3xl shadow-2xl p-8 mb-8 border-4 border-purple-300 hover:border-purple-400 transition-all duration-300 hover:shadow-3xl transform hover:-translate-y-1">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-xl transform hover:rotate-12 transition-transform duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-gray-800 tracking-tight">🎯 Search & Filter</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Institution Dropdown */}
            <div className="transform transition-all hover:scale-[1.02]">
              <label className="block text-sm font-bold text-purple-700 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
                Institution
              </label>
              <div className="relative">
                <select
                  value={selectedInstitution}
                  onChange={(e) => {
                    setSelectedInstitution(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-5 py-3 border-3 border-purple-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-400 focus:border-purple-500 shadow-sm hover:shadow-md transition-all bg-white appearance-none font-medium cursor-pointer"
                >
                  <option value="">🌐 All Institutions</option>
                  {getInstitutions().map(inst => (
                    <option key={inst.id} value={inst.id}>
                      🏛️ {inst.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="transform transition-all hover:scale-[1.02]">
              <label className="block text-sm font-bold text-purple-700 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search by Student Name, Roll Number, or Certificate Type
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="🔍 Type student name, roll number, or certificate type..."
                  className="w-full px-5 py-3 pl-12 pr-12 border-3 border-purple-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-400 focus:border-purple-500 shadow-sm hover:shadow-md transition-all font-medium"
                />
                {isLoading ? (
                  <svg
                    className="absolute left-4 top-3.5 h-6 w-6 text-purple-600 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    className="absolute left-4 top-3.5 h-6 w-6 text-purple-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                )}
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setPage(1);
                    }}
                    className="absolute right-4 top-3.5 p-1 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-all"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 border-2 border-purple-200">
            <div className="flex items-center gap-3">
              {isLoading ? (
                <span className="flex items-center gap-2 text-purple-700 font-medium">
                  <svg className="animate-spin h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching database...
                </span>
              ) : (
                <>
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-purple-900 font-bold text-lg">
                      Found <span className="text-2xl text-purple-700">{totalItems}</span> certificate{totalItems !== 1 ? 's' : ''}
                    </p>
                    {(selectedInstitution || searchQuery) && (
                      <p className="text-purple-600 text-sm">
                        {selectedInstitution && '📍 Filtered by institution'}
                        {selectedInstitution && searchQuery && ' • '}
                        {searchQuery && `🔍 Searching: "${searchQuery}"`}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            {(searchQuery || selectedInstitution) && !isLoading && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedInstitution('');
                  setPage(1);
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-3xl shadow-2xl overflow-hidden border-4 border-purple-300 hover:border-purple-400 transition-all duration-300 hover:shadow-3xl">
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 px-8 py-5 shadow-xl">
            <h3 className="text-3xl font-black text-white flex items-center gap-4 drop-shadow-lg">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Search Results
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-purple-200">
              <thead className="bg-gradient-to-r from-purple-100 to-pink-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-purple-800 uppercase tracking-wider border-r border-purple-200">
                    👤 Student Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-purple-800 uppercase tracking-wider border-r border-purple-200">
                    🎓 Roll Number
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-purple-800 uppercase tracking-wider border-r border-purple-200">
                    🏛️ Institution
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-purple-800 uppercase tracking-wider border-r border-purple-200">
                    📜 Certificate Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-purple-800 uppercase tracking-wider border-r border-purple-200">
                    📅 Upload Date
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-purple-800 uppercase tracking-wider">
                    ⚡ Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-20 text-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
                      <div className="flex flex-col items-center justify-center gap-6">
                        <div className="relative">
                          <div className="absolute inset-0 bg-purple-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
                          <svg className="animate-spin h-16 w-16 text-purple-600 relative z-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        </div>
                        <p className="text-purple-700 font-bold text-xl animate-pulse">Loading certificates...</p>
                      </div>
                    </td>
                  </tr>
                ) : certificates.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-20 text-center bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
                      <div className="flex flex-col items-center gap-6">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-xl">
                          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-800 font-black text-2xl mb-2">📋 No certificates found</p>
                          <p className="text-gray-600 font-medium text-lg">Try adjusting your search criteria or filters</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  certificates.map((cert, index) => (
                    <tr key={cert.id || index} className="hover:bg-gradient-to-r hover:from-purple-50 hover:via-pink-50 hover:to-blue-50 transition-all duration-200 transform hover:scale-[1.005] hover:shadow-lg">
                      <td className="px-6 py-5 whitespace-nowrap border-r border-purple-100">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-700 rounded-full flex items-center justify-center text-white font-bold shadow-xl border-2 border-blue-300">
                            {(cert.studentName || cert.student_name || 'N')?.[0]?.toUpperCase()}
                          </div>
                          <div className="text-sm font-bold text-gray-900 hover:text-purple-600 transition-colors">{cert.studentName || cert.student_name || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap border-r border-purple-100">
                        <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-100 to-purple-200 text-purple-900 text-sm font-bold shadow-md border border-purple-300">
                          {cert.rollNumber || cert.roll_number || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap border-r border-purple-100">
                        <div className="text-sm font-medium text-gray-900">{cert.institution || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap border-r border-purple-100">
                        <div className="inline-flex items-center px-4 py-1.5 rounded-xl bg-gradient-to-r from-green-100 to-emerald-200 text-green-900 text-sm font-bold shadow-md border-2 border-green-400">
                          {cert.certificateType || cert.certificate_type || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap border-r border-purple-100">
                        <div className="text-sm text-gray-600 font-medium">{formatDate(cert.uploadDate || cert.upload_date)}</div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleViewDetails(cert)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:scale-110 hover:-translate-y-0.5 border-2 border-orange-400">
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && certificates.length > 0 && (
            <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-indigo-100 px-8 py-6 flex items-center justify-between border-t-4 border-purple-400 sm:px-10 shadow-inner">
              <div className="flex-1 flex justify-between gap-3 sm:hidden">
                <button
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                  className="relative inline-flex items-center gap-2 px-5 py-3 border-3 border-purple-400 text-sm font-bold rounded-xl text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center gap-2 px-5 py-3 border-3 border-purple-400 text-sm font-bold rounded-xl text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all"
                >
                  Next
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-purple-900">
                      Showing <span className="text-lg text-purple-700">{(page - 1) * pageSize + 1}</span> to{' '}
                      <span className="text-lg text-purple-700">{Math.min(page * pageSize, totalItems)}</span> of{' '}
                      <span className="text-lg text-purple-700">{totalItems}</span> results
                    </p>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-2xl shadow-xl overflow-hidden border-3 border-purple-400" aria-label="Pagination">
                    <button
                      onClick={handlePreviousPage}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-3 border-r-2 border-purple-300 bg-gradient-to-r from-purple-500 to-purple-600 text-sm font-bold text-white hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <span className="relative inline-flex items-center px-6 py-3 bg-white text-base font-bold text-purple-900 border-r-2 border-purple-300">
                      Page <span className="text-2xl text-purple-600 mx-2">{page}</span> of <span className="text-2xl text-purple-600 mx-2">{totalPages}</span>
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center px-4 py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-sm font-bold text-white hover:from-pink-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Certificate Details Modal */}
        {showModal && selectedCertificate && (
        <div className="fixed z-50 inset-0 overflow-y-auto animate-fade-in">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-purple-900 bg-opacity-80 backdrop-blur-sm transition-opacity" onClick={() => setShowModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border-4 border-purple-200 animate-scale-in">
              <div className="bg-gradient-to-br from-white to-purple-50 px-6 pt-6 pb-6 sm:p-8 sm:pb-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-3xl leading-6 font-black text-gray-900">
                        📜 Certificate Details
                      </h3>
                    </div>
                    
                    <div className="mt-6 space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border-2 border-blue-200 shadow-sm">
                          <label className="block text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">👤 Student Name</label>
                          <p className="text-gray-900 font-bold text-lg">{selectedCertificate.studentName || selectedCertificate.student_name || 'N/A'}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border-2 border-purple-200 shadow-sm">
                          <label className="block text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">🎓 Roll Number</label>
                          <p className="text-gray-900 font-bold text-lg">{selectedCertificate.rollNumber || selectedCertificate.roll_number || 'N/A'}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border-2 border-green-200 shadow-sm">
                          <label className="block text-xs font-bold text-green-700 uppercase tracking-wide mb-2">🏛️ Institution</label>
                          <p className="text-gray-900 font-bold text-lg">{selectedCertificate.institution || 'N/A'}</p>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border-2 border-orange-200 shadow-sm">
                          <label className="block text-xs font-bold text-orange-700 uppercase tracking-wide mb-2">📜 Certificate Type</label>
                          <p className="text-gray-900 font-bold text-lg">{selectedCertificate.certificateType || selectedCertificate.certificate_type || 'N/A'}</p>
                        </div>
                      </div>

                      {selectedCertificate.fileHash && (
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl border-2 border-gray-300 shadow-sm">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">🔒 File Hash</label>
                          <p className="text-gray-900 font-mono text-xs break-all bg-white p-3 rounded-lg shadow-inner border border-gray-200">
                            {selectedCertificate.fileHash || selectedCertificate.file_hash}
                          </p>
                        </div>
                      )}

                      {selectedCertificate.cid && (
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 rounded-xl border-2 border-indigo-300 shadow-sm">
                          <label className="block text-xs font-bold text-indigo-700 uppercase tracking-wide mb-3">🌐 IPFS CID</label>
                          <div className="flex items-center gap-3">
                            <p className="text-gray-900 font-mono text-xs break-all bg-white p-3 rounded-lg shadow-inner border border-indigo-200 flex-1">
                              {selectedCertificate.cid}
                            </p>
                            <a
                              href={selectedCertificate.ipfsUrl || selectedCertificate.ipfs_url || `https://gateway.pinata.cloud/ipfs/${selectedCertificate.cid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white text-sm font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                              View 🔗
                            </a>
                          </div>
                        </div>
                      )}

                      {(selectedCertificate.anchorUrl || selectedCertificate.anchor_url) && (
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-5 rounded-xl border-2 border-yellow-300 shadow-sm">
                          <label className="block text-xs font-bold text-yellow-800 uppercase tracking-wide mb-3">⛓️ Blockchain Anchor</label>
                          <a
                            href={selectedCertificate.anchorUrl || selectedCertificate.anchor_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                          >
                            View on Blockchain 🔗
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-purple-50 px-6 py-5 sm:px-8 sm:flex sm:flex-row-reverse gap-4 border-t-2 border-purple-200">
                <button
                  onClick={handleDownload}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-green-400 shadow-lg px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-base font-bold text-white hover:from-green-600 hover:to-emerald-700 focus:outline-none sm:ml-3 sm:w-auto transform hover:scale-105 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-400 shadow-lg px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-base font-bold text-gray-800 hover:from-gray-200 hover:to-gray-300 focus:outline-none sm:mt-0 sm:w-auto transform hover:scale-105 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default HRSearchPage;
